import { useLazyQuery, useQuery, useMutation } from "@apollo/client";
import { useEffect, useState } from "react";

import { ICampaign, ILeaderboardDetails } from "../../@types";
import {
  GET_VIEWER_LEADERBOARD_DETAILS,
  GET_CAMPAIGN,
  SUBMIT_STEP_URL,
} from "MVVM/GraphQL/campaign";
import { JOIN_CAMPAIGN } from "MVVM/GraphQL/competitions";

export const PlayViewModel = (campaign: string, autojoin: boolean = false) => {
  const [isUserValidated, setIsUserValidated] = useState(false);

  const [getViewerLeaderboardDetails] = useLazyQuery<{
    campaign: {
      leaderboardDetails: {
        viewerContestant: ILeaderboardDetails["viewerContestant"];
        campaign: ILeaderboardDetails["campaign"];
      };
    };
  }>(GET_VIEWER_LEADERBOARD_DETAILS, {
    variables: { slug: campaign },
    fetchPolicy: "network-only",
  });

  const [joinCampaign] = useMutation(JOIN_CAMPAIGN, {
    variables: { campaign },
  });

  const [submitStepUrl, { loading: submittingUrl }] =
    useMutation(SUBMIT_STEP_URL);

  const { data, loading, error, refetch } = useQuery<{
    campaign: ICampaign;
  }>(GET_CAMPAIGN, {
    variables: { slug: campaign },
    skip: !campaign || !isUserValidated,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    const checkAndJoinCampaign = async () => {
      if (!campaign) return;

      const { data: leaderboardData } = await getViewerLeaderboardDetails();
      const viewerContestant =
        leaderboardData?.campaign?.leaderboardDetails?.viewerContestant;

      if (viewerContestant?.rank === -1) {
        if (autojoin) {
          await joinCampaign();
        }
        await getViewerLeaderboardDetails();
      }

      setIsUserValidated(true);
    };

    checkAndJoinCampaign();
  }, [campaign, getViewerLeaderboardDetails, joinCampaign, autojoin]);

  const validateUrl = (url: string, allowedDomains: string[]): boolean => {
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();

      return allowedDomains.some((domain) => {
        const normalizedDomain = domain.toLowerCase().replace(/^www\./, "");
        return (
          hostname === normalizedDomain ||
          hostname.endsWith("." + normalizedDomain)
        );
      });
    } catch {
      return false;
    }
  };

  const handleUrlSubmit = async (
    stepId: string,
    url: string,
    allowedDomains: string[],
  ) => {
    if (!validateUrl(url, allowedDomains)) {
      throw new Error("Invalid URL");
    }

    try {
      const result = await submitStepUrl({
        variables: {
          stepId,
          url,
          campaignSlug: campaign,
        },
      });

      if (result.data?.submitStepUrl?.success) {
        await refetch();
        return { success: true };
      } else {
        throw new Error(
          result.data?.submitStepUrl?.message || "Failed to submit URL",
        );
      }
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to submit URL. Please try again.",
      );
    }
  };

  return {
    campaign: data?.campaign,
    loading,
    error,
    refetch,
    getViewerLeaderboardDetails,
    joinCampaign,
    submitStepUrl: handleUrlSubmit,
    submittingUrl: loading || submittingUrl,
    validateUrl,
  };
};
